import React, { useEffect, useRef, useState } from 'react';
import {
  CHART_STYLES, getChartStyle, setChartStyle,
  DEFAULT_TRADE_MARKER_COLOR, getTradeMarkerColors, setTradeMarkerColors,
} from '../settings';
import { api } from '../api/client';
import { DeleteIcon } from '../components/Icons';
import MoneyMarketRates from '../components/MoneyMarketRates';

const MAX_MOVING_AVERAGES = 5;
const DEFAULT_COLORS = ['#3b82f6', '#f59e0b', '#22c55e', '#ef4444', '#a855f7'];
const AUTOSAVE_DELAY = 600;

let nextKey = 1;

export default function Configuration() {
  const [chartStyle, setChartStyleState] = useState(getChartStyle());
  const [markerColors, setMarkerColors] = useState(getTradeMarkerColors);

  const [maList, setMaList] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const isInitialLoad = useRef(true);

  const [mmSymbols, setMmSymbols] = useState(null);
  const [mmInput, setMmInput] = useState('');
  const [mmError, setMmError] = useState('');
  const [mmRatesSymbol, setMmRatesSymbol] = useState(null);

  useEffect(() => {
    api.getMoneyMarketSymbols().then(setMmSymbols).catch(() => setMmSymbols([]));
  }, []);

  const addMmSymbol = async () => {
    const symbol = mmInput.trim().toUpperCase();
    if (!symbol) return;
    try {
      setMmSymbols(await api.addMoneyMarketSymbol(symbol));
      setMmInput('');
      setMmError('');
    } catch {
      setMmError('Could not add that symbol.');
    }
  };

  const removeMmSymbol = async (symbol) => {
    try {
      setMmSymbols(await api.removeMoneyMarketSymbol(symbol));
      setMmError('');
    } catch {
      setMmError('Could not remove that symbol.');
    }
  };

  useEffect(() => {
    api.getMovingAverages()
      .then(rows => setMaList(rows.map(r => ({ key: nextKey++, ma_type: r.ma_type, period: r.period, color: r.color }))))
      .catch(() => setMaList([]));
  }, []);

  // Autosaves maList to the backend, debounced so rapid edits (e.g. typing a
  // period) collapse into one request instead of firing on every keystroke.
  useEffect(() => {
    if (maList === null) return;
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    setSaveStatus('saving');
    const timer = setTimeout(async () => {
      try {
        const payload = maList.map(({ ma_type, period, color }) => ({ ma_type, period: Number(period), color }));
        await api.setMovingAverages(payload);
        setSaveStatus('saved');
      } catch {
        setSaveStatus('error');
      }
    }, AUTOSAVE_DELAY);
    return () => clearTimeout(timer);
  }, [maList]);

  useEffect(() => {
    if (saveStatus !== 'saved') return;
    const timer = setTimeout(() => setSaveStatus('idle'), 2000);
    return () => clearTimeout(timer);
  }, [saveStatus]);

  const choose = (id) => {
    setChartStyle(id);
    setChartStyleState(id);
  };

  const updateMarkerColor = (type, color) => {
    const next = { ...markerColors, [type]: color };
    setTradeMarkerColors(next);
    setMarkerColors(next);
  };

  const addMa = () => {
    if (maList.length >= MAX_MOVING_AVERAGES) return;
    setMaList([...maList, {
      key: nextKey++,
      ma_type: 'SMA',
      period: 20,
      color: DEFAULT_COLORS[maList.length % DEFAULT_COLORS.length],
    }]);
  };

  const updateMa = (key, field, value) => {
    setMaList(maList.map(m => m.key === key ? { ...m, [field]: value } : m));
  };

  const removeMa = (key) => {
    setMaList(maList.filter(m => m.key !== key));
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Configuration</h1>
      </div>

      <div style={{ display: 'grid', gap: 20, maxWidth: 640 }}>
        <div className="card">
          <div className="modal-title">Chart Style</div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
            Choose how price series are drawn on the Charts page.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {CHART_STYLES.map(s => (
              <button
                key={s.id}
                onClick={() => choose(s.id)}
                className={s.id === chartStyle ? 'btn' : 'btn-ghost'}
                style={{ textAlign: 'left', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 2, height: 'auto' }}
              >
                <span style={{ fontWeight: 600 }}>{s.label}</span>
                <span
                  className={s.id === chartStyle ? '' : 'muted'}
                  style={{ fontSize: 12, fontWeight: 400, opacity: s.id === chartStyle ? 0.85 : 1 }}
                >
                  {s.description}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="modal-title">Trade Markers</div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
            Colors of the buy and sell markers drawn at your transactions on the Charts page.
          </div>

          <div style={{ display: 'grid', gap: 10 }}>
            {[['buy', 'Buy'], ['sell', 'Sell']].map(([type, label]) => (
              <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 90, fontSize: 13 }}>{label}</span>
                <input
                  type="color"
                  value={markerColors[type]}
                  onChange={e => updateMarkerColor(type, e.target.value)}
                  style={{ width: 44, height: 32, padding: 2, cursor: 'pointer' }}
                />
                {markerColors[type] !== DEFAULT_TRADE_MARKER_COLOR && (
                  <button
                    className="btn-ghost"
                    style={{ fontSize: 12, padding: '3px 10px' }}
                    onClick={() => updateMarkerColor(type, DEFAULT_TRADE_MARKER_COLOR)}
                  >
                    Reset
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="modal-title">Money Market Symbols</div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
            Money market and sweep funds such as SPAXX or FDRXX. Prices are never fetched for these symbols; their history is loaded from files on the Data page.
          </div>

          {mmSymbols === null ? (
            <div className="muted">Loading...</div>
          ) : (
            <>
              {mmSymbols.length === 0 && (
                <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>No money market symbols yet.</div>
              )}
              <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
                {mmSymbols.map(m => (
                  <div key={m.symbol} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <strong className="mono" style={{ width: 70 }}>{m.symbol}</strong>
                    {m.rate_pct != null ? (
                      <span style={{ fontSize: 13 }}>
                        <span className="mono">{m.rate_pct.toFixed(2)}%</span>
                        <span className="muted"> 7-day yield, as of {m.rate_date}</span>
                      </span>
                    ) : (
                      <span className="muted" style={{ fontSize: 13 }}>No yield loaded yet</span>
                    )}
                    <button
                      className="btn-ghost"
                      style={{ marginLeft: 'auto', fontSize: 12, padding: '3px 10px' }}
                      onClick={() => setMmRatesSymbol(m.symbol)}
                    >
                      Rates ({m.rate_count})
                    </button>
                    <button
                      className="icon-btn-sm"
                      title="Remove"
                      aria-label="Remove"
                      onClick={() => removeMmSymbol(m.symbol)}
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  value={mmInput}
                  onChange={e => setMmInput(e.target.value.toUpperCase())}
                  onKeyDown={e => { if (e.key === 'Enter') addMmSymbol(); }}
                  placeholder="SPAXX"
                  style={{ width: 140 }}
                />
                <button className="btn-ghost" onClick={addMmSymbol} disabled={!mmInput.trim()}>+ Add Symbol</button>
              </div>
              {mmError && <div className="loss" style={{ fontSize: 12, marginTop: 8 }}>{mmError}</div>}
            </>
          )}
        </div>

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <div className="modal-title">Moving Averages</div>
            {saveStatus !== 'idle' && (
              <span
                className={saveStatus === 'error' ? 'loss' : 'muted'}
                style={{ fontSize: 12 }}
              >
                {saveStatus === 'saving' && 'Saving...'}
                {saveStatus === 'saved' && 'Saved'}
                {saveStatus === 'error' && 'Could not save'}
              </span>
            )}
            {maList !== null && (
              <button
                className="btn-ghost"
                style={{ marginLeft: 'auto' }}
                onClick={addMa}
                disabled={maList.length >= MAX_MOVING_AVERAGES}
              >
                + Add Moving Average
              </button>
            )}
          </div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
            Overlay up to {MAX_MOVING_AVERAGES} SMA/EMA lines on the Charts page. Changes save automatically to your account.
          </div>

          {maList === null ? (
            <div className="muted">Loading...</div>
          ) : (
            <>
              {maList.length === 0 && (
                <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>No moving averages configured.</div>
              )}

              <div style={{ display: 'grid', gap: 10 }}>
                {maList.map(m => (
                  <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <select
                      value={m.ma_type}
                      onChange={e => updateMa(m.key, 'ma_type', e.target.value)}
                      style={{ width: 90 }}
                    >
                      <option value="SMA">SMA</option>
                      <option value="EMA">EMA</option>
                    </select>
                    <input
                      type="number"
                      min={1}
                      max={500}
                      value={m.period}
                      onChange={e => updateMa(m.key, 'period', e.target.value)}
                      style={{ width: 80 }}
                    />
                    <span className="muted" style={{ fontSize: 13 }}>period</span>
                    <input
                      type="color"
                      value={m.color}
                      onChange={e => updateMa(m.key, 'color', e.target.value)}
                      style={{ width: 44, height: 32, padding: 2, cursor: 'pointer' }}
                    />
                    <button
                      className="icon-btn-sm"
                      style={{ marginLeft: 'auto' }}
                      title="Remove"
                      aria-label="Remove"
                      onClick={() => removeMa(m.key)}
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {mmRatesSymbol && (
        <MoneyMarketRates
          symbol={mmRatesSymbol}
          onClose={() => setMmRatesSymbol(null)}
          onChanged={() => api.getMoneyMarketSymbols().then(setMmSymbols).catch(() => {})}
        />
      )}
    </div>
  );
}
